import {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import {
    AppError,
    parseAppError,
} from "../../../../../../errors/errorProcessors"
import { useFilterReferenceMappings } from "../../../../../../hooks/useFilterReferenceMappings"
import { useGetUserData } from "../../../../../../hooks/useGetUserData"
import usePublishLCTDevice from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDevice"
import useDeviceReference from "../../../../../../services/middleware/hooks/devices/ttr/useDeviceReference"
import usePublishDevice from "../../../../../../services/middleware/hooks/devices/ttr/usePublishDevice"
import {
    checkContextErrors,
    getEmptyEVACDeviceFormData,
    getEmptyTTRDeviceFormData,
} from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDatabaseSearchResult } from "../../../../../tables/ttr/genSearchResults"
import { EVACDeviceFormData } from "../../../types/providerTypes"
import {
    IV2GBundleContextValue,
    V2GBundleGenerationMode,
} from "../../types/providerTypes"

interface ICreateV2GBundleProviderProps {
    children: ReactNode
    evacAttachmentUUID: string
}

const getEmptyBundleEvacFormData = (): EVACDeviceFormData => ({
    ...getEmptyEVACDeviceFormData(),
    v2gCapability: true,
})

export const CreateV2GBundleContext = createContext<IV2GBundleContextValue>(
    {} as IV2GBundleContextValue
)

const CreateV2GBundleProvider = ({
    children,
    evacAttachmentUUID,
}: ICreateV2GBundleProviderProps): ReactElement => {
    const [currentIndex, setCurrentIndexState] = useState(0)
    const [errors, setErrors] = useState<AppError[]>([])
    const [generationMode, setGenerationMode] =
        useState<V2GBundleGenerationMode>(null)
    const [selectedInverter, setSelectedInverter] =
        useState<IDatabaseSearchResult | null>(null)
    const [createdEvacDeviceRef, setCreatedEvacDeviceRef] = useState<
        string | undefined
    >(undefined)
    const [createdGenDeviceRef, setCreatedGenDeviceRef] = useState<
        string | undefined
    >(undefined)

    const [evacFormData, setEvacFormData] = useState(getEmptyBundleEvacFormData)
    const [ttrFormData, setTtrFormData] = useState(getEmptyTTRDeviceFormData)

    const { isEnaSysAdmin, isManuApproved } = useGetUserData()

    const {
        deviceCategoryMappings,
        deviceTypeMappings,
        isLoading: referenceDataLoading,
    } = useFilterReferenceMappings()

    const inverterCategoryId = useMemo(() => {
        const entry = Object.entries(deviceCategoryMappings).find(
            ([, value]) => String(value).toLowerCase() === "inverter"
        )
        return entry ? Number(entry[0]) : null
    }, [deviceCategoryMappings])

    const v2gDeviceTypeId = useMemo(() => {
        const entry = Object.entries(deviceTypeMappings).find(([, value]) =>
            String(value).toLowerCase().includes("vehicle to grid")
        )
        return entry ? Number(entry[0]) : null
    }, [deviceTypeMappings])

    const doesNotNeedManufacturerId = !isEnaSysAdmin && isManuApproved
    const manufacturerId = doesNotNeedManufacturerId
        ? undefined
        : ttrFormData.coreDetails.manufacturer

    const {
        isValidating: ttrRefIsValidating,
        isLoading: ttrRefIsLoading,
        error: ttrRefError,
        deviceId: generatedTtrDeviceId,
        deviceRef: generatedTtrDeviceRef,
    } = useDeviceReference(manufacturerId)

    const {
        publishDevice,
        isLoading: publishGenInProgress,
        error: publishGenError,
    } = usePublishDevice(
        ttrFormData,
        generatedTtrDeviceId,
        generatedTtrDeviceRef
    )

    const {
        publishLCTDevice,
        isLoading: publishEvacInProgress,
        error: publishEvacError,
    } = usePublishLCTDevice(
        evacFormData,
        LCTOnlyDatasets.EVAC,
        evacAttachmentUUID
    )

    checkContextErrors(
        [ttrRefError, publishGenError, publishEvacError],
        errors,
        (error) => setErrors((prev) => [...prev, error])
    )

    const setCurrentIndex = useCallback((step: number) => {
        setErrors([])
        setCurrentIndexState(step)
    }, [])

    const onSubmit = useCallback(async () => {
        try {
            if (generationMode === "existing") {
                if (!selectedInverter?.deviceReference) {
                    return
                }

                const response = await publishLCTDevice({
                    ...evacFormData,
                    v2gCapability: true,
                    v2gTtrStatus: selectedInverter.deviceReference,
                })

                setCreatedEvacDeviceRef(response.deviceRef)
                return
            }

            if (generationMode === "new") {
                if (!generatedTtrDeviceId || !generatedTtrDeviceRef) {
                    return
                }

                await publishDevice()

                setCreatedGenDeviceRef(generatedTtrDeviceRef)

                const response = await publishLCTDevice({
                    ...evacFormData,
                    v2gCapability: true,
                    v2gTtrStatus: generatedTtrDeviceRef,
                })

                setCreatedEvacDeviceRef(response.deviceRef)
            }
        } catch (error) {
            setErrors((prev) => [...prev, parseAppError(error)])
        }
    }, [
        generationMode,
        selectedInverter,
        publishLCTDevice,
        evacFormData,
        generatedTtrDeviceId,
        generatedTtrDeviceRef,
        publishDevice,
    ])

    const submitLoading = publishGenInProgress || publishEvacInProgress

    const submitSuccessful =
        !!createdEvacDeviceRef &&
        (generationMode === "existing" ||
            (generationMode === "new" && !!createdGenDeviceRef))

    const formIsLoading =
        generationMode === "new" && (ttrRefIsLoading || ttrRefIsValidating)

    const value: IV2GBundleContextValue = useMemo(
        () => ({
            currentIndex,
            setCurrentIndex,
            canEditManufacturer: isEnaSysAdmin,
            evacFormData,
            setEvacFormData,
            ttrFormData,
            setTtrFormData,
            generationMode,
            setGenerationMode,
            selectedInverter,
            setSelectedInverter,
            evacAttachmentUUID,
            inverterCategoryId,
            v2gDeviceTypeId,
            referenceDataLoading,
            generatedTtrDeviceId,
            generatedTtrDeviceRef,
            createdEvacDeviceRef,
            createdGenDeviceRef,
            onSubmit,
            submitLoading,
            submitSuccessful,
            formIsLoading,
            errors,
        }),
        [
            currentIndex,
            setCurrentIndex,
            isEnaSysAdmin,
            evacFormData,
            ttrFormData,
            generationMode,
            selectedInverter,
            evacAttachmentUUID,
            inverterCategoryId,
            v2gDeviceTypeId,
            referenceDataLoading,
            generatedTtrDeviceId,
            generatedTtrDeviceRef,
            createdEvacDeviceRef,
            createdGenDeviceRef,
            onSubmit,
            submitLoading,
            submitSuccessful,
            formIsLoading,
            errors,
        ]
    )

    return (
        <CreateV2GBundleContext.Provider value={value}>
            {children}
        </CreateV2GBundleContext.Provider>
    )
}

export default CreateV2GBundleProvider
